import axios from "axios";
import { getApiBase } from "@/config/api";
import store from "../store/index.js";

const AUTH_TOKEN = process.env.VUE_APP_AUTH_TOKEN;

const apiClient = axios.create({
  baseURL: getApiBase(),
  headers: {
    Authorization: `Bearer ${AUTH_TOKEN}`,
  },
});

const getLanguageCode = () => {
  const activeLanguage = store.getters["languages/activeLanguage"];
  return activeLanguage?.two_digit_code || "en";
};

const getCurrentSectorId = () => {
  const currentSector = store.getters["processes/currentSector"];
  return currentSector?.id || null;
};

const createRequest = (endpoint, params = {}) => {
  const url = new URL(endpoint, getApiBase());
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });
  return apiClient.get(url.pathname + url.search);
};

const withLanguage = (params = {}) => ({
  ...params,
  lang: getLanguageCode(),
});

const withLanguageAndSector = (params = {}) => {
  const sectorId = getCurrentSectorId();
  return {
    ...params,
    lang: getLanguageCode(),
    ...(sectorId && { sector: sectorId }),
  };
};

export const getImage = async (sectorId) => {
  const response = await createRequest("/v1/process/view", withLanguage({ id: sectorId }));
  return { ...response.data, src: response.data.image };
};

export const getPeg = (slug) => 
  createRequest(`/v1/hotspotprocess/sector-content/${slug}`, withLanguage({ scenario: "peg" }))
    .then(response => response.data);

export const getSolutionHotspot = (slug) =>
  createRequest(`/v1/content/individual-content/${slug}`, withLanguageAndSector({ scenario: "solution-modal" }))
    .then(response => response.data);

export const getProcessHotspot = (slug) =>
  createRequest(`/v1/content/individual-content/${slug}`, withLanguage({ scenario: "process-modal" }))
    .then(response => response.data);

export const getLayers = () =>
  createRequest("/v1/layer", withLanguage())
    .then(response => response.data);

export const getNextContent = (slug) =>
  createRequest(`/v1/content/next-content/${slug}`, withLanguage())
    .then(response => response.data);

export const getPreviousContent = (slug) =>
  createRequest(`/v1/content/previous-content/${slug}`, withLanguage())
    .then(response => response.data);

export const getStaticContent = () =>
  createRequest("/v1/translation/static-content", withLanguage())
    .then(response => response.data);

export const getCategory = (slug) =>
  createRequest(`/v1/content/individual-content/${slug}`, withLanguageAndSector())
    .then(response => response.data);

export const getCategories = () =>
  createRequest("/v1/category", withLanguage())
    .then(response => response.data);

export const getPortfolios = () =>
  createRequest("/v1/portfolio", withLanguage())
    .then(response => response.data);

export const getLanguages = () =>
  createRequest("/v1/language")
    .then(response => response.data);

export const getValueDrivers = () =>
  createRequest("/v1/valuedriver", withLanguageAndSector())
    .then(response => response.data);

export const checkHotspots = (sector_id) =>
  createRequest(`/v1/process/check-hotspots/${sector_id}`)
    .then(response => response.data);